/**
 * useOverlayHistory - Undo/Redo management for overlay editing
 * Elvis Aloha Collection - Arena Map Editor
 * 
 * Manages a stack-based undo/redo system with chart-scoped history.
 * Clears appropriately on chart switch and server reload.
 */

import { useState, useCallback, useRef, useMemo, useEffect } from 'react';

// History entry types for audit trail
export const HISTORY_ACTIONS = {
  CREATE: 'create',
  DELETE: 'delete',
  MOVE: 'move',
  RESIZE: 'resize',
  VERTEX_EDIT: 'vertex_edit',
  RENAME: 'rename',
  CATEGORY_CHANGE: 'category_change',
  STATUS_CHANGE: 'status_change',
  BULK_UPDATE: 'bulk_update'
};

class HistoryManager {
  constructor(maxSize = 50) {
    this.undoStack = [];
    this.redoStack = [];
    this.maxSize = maxSize;
  }
  
  push(state, action, metadata = {}) {
    const entry = {
      state: JSON.stringify(state),
      action,
      metadata,
      timestamp: Date.now()
    };
    
    this.undoStack.push(entry);
    if (this.undoStack.length > this.maxSize) {
      this.undoStack.shift();
    }
    this.redoStack = []; // Clear redo on new action
  }
  
  undo(currentState) {
    if (this.undoStack.length === 0) return null;
    
    const entry = this.undoStack.pop();
    this.redoStack.push({
      state: JSON.stringify(currentState),
      action: 'redo_point',
      timestamp: Date.now()
    });
    
    return {
      state: JSON.parse(entry.state),
      action: entry.action,
      metadata: entry.metadata
    };
  }
  
  redo(currentState) {
    if (this.redoStack.length === 0) return null;
    
    const entry = this.redoStack.pop();
    this.undoStack.push({
      state: JSON.stringify(currentState),
      action: 'undo_point',
      timestamp: Date.now()
    });
    
    return {
      state: JSON.parse(entry.state),
      action: entry.action
    };
  }
  
  canUndo() { return this.undoStack.length > 0; }
  canRedo() { return this.redoStack.length > 0; }
  
  clear() {
    this.undoStack = [];
    this.redoStack = [];
  }
  
  getUndoCount() { return this.undoStack.length; }
  getRedoCount() { return this.redoStack.length; }
}

export const useOverlayHistory = (chartId) => {
  // One history manager per chart
  const historyManagersRef = useRef({});
  const [undoCount, setUndoCount] = useState(0);
  const [redoCount, setRedoCount] = useState(0);
  
  // Get or create history manager for current chart
  const getManager = useCallback(() => {
    if (!chartId) return null;
    
    if (!historyManagersRef.current[chartId]) {
      historyManagersRef.current[chartId] = new HistoryManager();
    }
    return historyManagersRef.current[chartId];
  }, [chartId]);
  
  // Sync counts when chartId changes - FIX for history isolation bug
  useEffect(() => {
    const manager = historyManagersRef.current[chartId];
    if (manager) {
      setUndoCount(manager.getUndoCount());
      setRedoCount(manager.getRedoCount());
    } else {
      // New chart with no history yet
      setUndoCount(0);
      setRedoCount(0);
    }
  }, [chartId]);
  
  /**
   * Push a state snapshot before making changes
   */
  const pushState = useCallback((overlays, action = 'edit', metadata = {}) => {
    const manager = getManager();
    if (!manager) return;
    
    manager.push(overlays, action, {
      ...metadata,
      chartId,
      timestamp: new Date().toISOString()
    });
    
    setUndoCount(manager.getUndoCount());
    setRedoCount(manager.getRedoCount());
  }, [getManager, chartId]);
  
  /**
   * Undo last action
   */
  const undo = useCallback((currentOverlays) => {
    const manager = getManager();
    if (!manager) return null;
    
    const result = manager.undo(currentOverlays);
    setUndoCount(manager.getUndoCount());
    setRedoCount(manager.getRedoCount());
    
    return result;
  }, [getManager]);
  
  /**
   * Redo last undone action
   */
  const redo = useCallback((currentOverlays) => {
    const manager = getManager();
    if (!manager) return null;
    
    const result = manager.redo(currentOverlays);
    setUndoCount(manager.getUndoCount());
    setRedoCount(manager.getRedoCount());
    
    return result;
  }, [getManager]);
  
  /**
   * Clear history for current chart (e.g., after server reload)
   */
  const clearHistory = useCallback(() => {
    const manager = getManager();
    if (!manager) return;
    
    manager.clear();
    setUndoCount(0);
    setRedoCount(0);
  }, [getManager]);
  
  /**
   * Clear all history (e.g., on logout)
   */
  const clearAllHistory = useCallback(() => {
    historyManagersRef.current = {};
    setUndoCount(0);
    setRedoCount(0);
  }, []);
  
  const canUndo = useMemo(() => undoCount > 0, [undoCount]);
  const canRedo = useMemo(() => redoCount > 0, [redoCount]);
  
  return {
    pushState,
    undo,
    redo,
    clearHistory,
    clearAllHistory,
    canUndo,
    canRedo,
    undoCount,
    redoCount
  };
};

export default useOverlayHistory;
